import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { UploadedFileItem } from '@ldt/features/orders/components/uploaded_file_item';
import { CloudUpload } from 'src/components/icons/cloud_upload';
import { UploadedFileCategory, LdtUploadedFileFieldsFragment } from 'src/types';

interface RevisionReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (qcRevisionReason: string) => Promise<void>;
  onFileSelected?: (file: File, category: UploadedFileCategory) => void;
  onFileRemoved?: (fileId: string) => void;
  isFileDeleting?: (fileId: string) => boolean;
  otherFiles?: LdtUploadedFileFieldsFragment[];
  /** Existing QC revision reason to prefill the field when present */
  existingQcRevisionReason?: string | null;
}

export const RevisionReasonDialog = ({
  open,
  onClose,
  onSubmit,
  onFileSelected,
  onFileRemoved,
  isFileDeleting,
  otherFiles = [],
  existingQcRevisionReason,
}: RevisionReasonDialogProps) => {
  const [revisionReason, setRevisionReason] = useState('');
  const [error, setError] = useState<string>('');

  // When dialog opens: preload existing reason if provided, otherwise reset
  useEffect(() => {
    if (open) {
      if (existingQcRevisionReason?.trim()) {
        setRevisionReason(existingQcRevisionReason.trim());
      } else {
        setRevisionReason('');
      }
      setError('');
    }
  }, [open, existingQcRevisionReason]);

  // File upload handlers for revision reason dialog
  const handleRevisionFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (onFileSelected) {
        for (const file of acceptedFiles) {
          onFileSelected(file, UploadedFileCategory.Other);
        }
      }
    },
    [onFileSelected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleRevisionFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    noClick: false,
    noDrag: false, // Enable drag and drop
    multiple: true,
  });

  const handleSubmit = async () => {
    // Validate max length
    if (revisionReason.length > 450) {
      setError('Revision reason cannot exceed 450 characters');
      return;
    }

    setError('');
    await onSubmit(revisionReason);
    // Clear revision reason after submitting
    setRevisionReason('');
  };

  const handleClose = () => {
    setRevisionReason('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ textAlign: 'left' }}>Revision Reason</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder='Type here...'
            value={revisionReason}
            onChange={(e) => {
              const value = e.target.value;
              setRevisionReason(value);
              // Clear error when user starts typing
              if (error && value.length <= 450) {
                setError('');
              }
            }}
            error={!!error}
            helperText={
              error ||
              (revisionReason.length > 0
                ? `${revisionReason.length}/450 characters`
                : ' ')
            }
            slotProps={{
              input: {
                inputProps: {
                  maxLength: 450,
                },
              },
            }}
          />

          {/* File drag and drop area for Other category */}
          <Box {...getRootProps()}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                border: (theme) =>
                  `1px dashed ${
                    isDragActive
                      ? theme.palette.text.primary
                      : theme.palette.primary.main
                  }`,
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundImage: 'none',
                '&:hover': {
                  borderColor: (theme) => theme.palette.text.primary,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.dark
                      : theme.palette.primary.light,
                },
              }}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <CloudUpload
                  sx={{
                    mr: 2,
                    fontSize: (theme) => theme.typography.h3.fontSize,
                    color: (theme) => theme.palette.text.secondary,
                  }}
                />
                <Typography
                  variant='body1'
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  Click or Drag and Drop to upload
                </Typography>
                <Typography
                  variant='subtitle2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  pdf / png / jpg / jpeg types are supported
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Display newly uploaded files (only files added during this dialog session) */}
          {otherFiles.length > 0 && (
            <Box
              sx={{
                mt: 1,
                maxHeight: '120px',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {otherFiles.map((uploadedFile, index) => (
                <UploadedFileItem
                  key={uploadedFile.id || index}
                  file={uploadedFile}
                  onRemove={onFileRemoved}
                  index={index}
                  isRemoving={isFileDeleting?.(uploadedFile.id)}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button variant='contained' onClick={handleSubmit} size='large'>
            SUBMIT
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
